/**
 * ScoringService — rubric explícita por caso completado.
 *
 * Antes: el score era binario (100 si correcto, 50 si no). Eso oculta al
 * alumno qué cuenta para el puntaje y no premia el razonamiento propio.
 *
 * Ahora: breakdown ponderado transparente:
 *
 *   Base                           +50
 *   Diagnóstico correcto           +30  (criterio principal)
 *   Triage correcto                +15  (gravedad bien clasificada)
 *   Tiempo < 5 min                 +5   (eficiencia)
 *   Penalidad "consultó IA antes"  -10  (razonamiento propio)
 *
 * Máximo 100, mínimo 0. El breakdown se devuelve completo para que el HUD
 * lo muestre al alumno al finalizar un caso.
 */

export interface ScoreBreakdownItem {
  label: string;
  points: number;
  /** true si el ítem aportó puntos, false si penalizó o no se ganó. */
  earned: boolean;
  /** Descripción breve para UI. */
  detail?: string;
}

export interface ScoreResult {
  total: number;               // 0-100
  items: ScoreBreakdownItem[];
  /** Suma de ganados (sin penalidades). Útil para mostrar "+80 ganados". */
  earnedTotal: number;
  /** Suma de penalidades. Útil para mostrar "-10 por consultar IA antes". */
  penaltyTotal: number;
}

export interface ScoreInput {
  diagnosisCorrect: boolean;
  triageCorrect: boolean;
  durationSeconds: number;
  consultedAIBeforeDx: boolean;
}

/** Umbral de "tiempo eficiente" — por debajo se otorga bonus. */
export const FAST_THRESHOLD_SEC = 300;

export function calculateScore(input: ScoreInput): ScoreResult {
  const items: ScoreBreakdownItem[] = [];

  items.push({
    label: 'Base por completar el caso',
    points: 50,
    earned: true,
  });

  items.push({
    label: 'Diagnóstico correcto',
    points: 30,
    earned: input.diagnosisCorrect,
    detail: input.diagnosisCorrect ? undefined : 'Revisá tu razonamiento con el Tutor.',
  });

  items.push({
    label: 'Triage correcto',
    points: 15,
    earned: input.triageCorrect,
    detail: input.triageCorrect ? undefined : 'Asignar la gravedad correcta es crítico en triage.',
  });

  const fast = input.durationSeconds <= FAST_THRESHOLD_SEC;
  items.push({
    label: 'Resolución eficiente (<5 min)',
    points: 5,
    earned: fast,
    detail: fast
      ? `Completado en ${Math.round(input.durationSeconds / 60)} min.`
      : `Demoraste ${Math.round(input.durationSeconds / 60)} min; meta <5 min.`,
  });

  items.push({
    label: 'Razonamiento propio (sin abrir IA antes del dx)',
    points: input.consultedAIBeforeDx ? -10 : 0,
    earned: !input.consultedAIBeforeDx,
    detail: input.consultedAIBeforeDx
      ? 'Consultaste el análisis IA antes de proponer tu dx.'
      : undefined,
  });

  let earnedTotal = 0;
  let penaltyTotal = 0;
  for (const it of items) {
    if (it.points > 0 && it.earned) earnedTotal += it.points;
    if (it.points < 0) penaltyTotal += it.points;
  }
  const total = Math.max(0, Math.min(100, earnedTotal + penaltyTotal));

  return { total, items, earnedTotal, penaltyTotal };
}

/**
 * Render HTML del breakdown para mostrar en el HUD al completar. Estilo
 * minimal: tabla de ítems ganados/perdidos con puntajes a la derecha.
 */
export function renderScoreBreakdown(result: ScoreResult): string {
  const rows = result.items.map(it => {
    const sign = it.points > 0 ? '+' : it.points < 0 ? '−' : '';
    const abs = Math.abs(it.points);
    const colorCls = it.earned && it.points > 0
      ? 'cgt-score-ok'
      : (it.points < 0 ? 'cgt-score-bad' : 'cgt-score-miss');
    return `
      <li class="${colorCls}">
        <span class="cgt-score-label">${escape(it.label)}</span>
        <span class="cgt-score-pts">${sign}${abs}</span>
        ${it.detail ? `<small>${escape(it.detail)}</small>` : ''}
      </li>
    `;
  }).join('');

  return `
    <div class="cgt-score-total">${result.total}<span>/100</span></div>
    <ul class="cgt-score-items">${rows}</ul>
  `;
}

function escape(text: string): string {
  return text.replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c] ?? c));
}
